// Открытые панели матча на время облета захваченных городов (см.
// scenes/CityCaptureAnimation) плавно гаснут, а после облета возвращаются.
// Панель помечает свой корень атрибутом, пока открыта (getCityCaptureFadeProps):
// выпадающие списки шапки (игроки, рынок, ресурсы), лист рынка на телефоне,
// карточки фракции и политики. Угол со слоями поля, реакциями и чатом и кнопка
// хода гаснут вместе с футером (Footer, app-footer_city-capture-hidden).
//
// Гасят и возвращают анимации Web Animations, а не классы и не инлайн-стили:
// - React их не видит и при перерисовке не сбросит, а свои переходы панелей
//   (открытие по opacity и transform) не перебиваются;
// - возврат идет к исходной прозрачности панели, какой бы она ни была:
//   снятая анимация отдает значение самим стилям панели;
// - opacity анимирует композитор, а погасшая панель в конце еще и скрыта
//   (visibility: hidden) — облет не платит за ее отрисовку поверх поля и не
//   ловит по ней нажатия. visibility — отдельной анимацией: свойство, которое
//   композитор не умеет, в одной анимации с opacity увело бы на основной поток
//   и ее.

export const CITY_CAPTURE_FADE_ATTRIBUTE = 'data-city-capture-fade';

/**
 * Атрибут корня панели: есть, пока панель открыта
 * @param {boolean} isOpen
 * @returns {Object} пропсы корня
 */
export const getCityCaptureFadeProps = (isOpen) => (isOpen ? {[CITY_CAPTURE_FADE_ATTRIBUTE]: true} : {});

const FADE_EASING = 'ease-in-out';

const fadeOutPanel = (element, durationMs) => {
  if (typeof element.animate === 'function') {
    const holdOptions = {duration: durationMs, easing: FADE_EASING, fill: 'forwards'};
    // Видна, пока гаснет, и скрыта с последнего кадра
    const holds = [
      element.animate([{opacity: 0}], holdOptions),
      element.animate([{visibility: 'hidden'}], holdOptions),
    ];
    return {
      release: ({fadeIn}) => {
        holds.forEach((hold) => hold.cancel());
        if (fadeIn) {
          // Ключевой кадр только начальный: конечное значение — свое у панели
          element.animate([{opacity: 0, offset: 0}], {duration: durationMs, easing: FADE_EASING});
        }
      },
    };
  }
  // Без Web Animations — просто скрыть и вернуть
  const {visibility} = element.style;
  element.style.visibility = 'hidden';
  return {
    release: () => {
      element.style.visibility = visibility;
    },
  };
};

/**
 * Погасить панели, открытые сейчас внутри `root`. Открытые позже не
 * трогаются: игрок открыл их сам, во время облета. Закрытая во время облета
 * панель отпускается сразу — откроют снова, и она покажется как обычно
 * @param {ParentNode} root - корень экрана матча
 * @param {number} durationMs - длительность угасания и возврата
 * @returns {function(): void} вернуть погашенные панели
 */
export const fadeOutCityCapturePanels = (root, durationMs) => {
  const fades = new Map();
  [...root.querySelectorAll(`[${CITY_CAPTURE_FADE_ATTRIBUTE}]`)].forEach((element) => {
    fades.set(element, fadeOutPanel(element, durationMs));
  });
  if (fades.size === 0) {
    return () => {};
  }

  const release = (element, options) => {
    const fade = fades.get(element);
    if (fade) {
      fades.delete(element);
      fade.release(options);
    }
  };

  const observer = typeof MutationObserver === 'function'
    ? new MutationObserver((records) => {
      records.forEach(({target}) => {
        if (!target.hasAttribute(CITY_CAPTURE_FADE_ATTRIBUTE)) {
          release(target, {fadeIn: false});
        }
      });
    })
    : null;
  fades.forEach((fade, element) => {
    observer?.observe(element, {attributes: true, attributeFilter: [CITY_CAPTURE_FADE_ATTRIBUTE]});
  });

  return () => {
    observer?.disconnect();
    [...fades.keys()].forEach((element) => {
      release(element, {fadeIn: element.isConnected && element.hasAttribute(CITY_CAPTURE_FADE_ATTRIBUTE)});
    });
  };
};
